import { type FC, type ReactNode, type ComponentProps, type RefObject } from 'react';

import {
    InternalScrollCarouselProvider,
    useInternalScrollCarousel,
} from '../helpers/InternalScrollCarouselContext.tsx';
import { Arrow } from '../Arrow/Arrow.tsx';
import { ScrollContainer } from '../ScrollContainer/ScrollContainer.tsx';

import { type OnArrowClick } from '../types.ts';

import styles from './InnerScrollCarousel.scss';

const Inner: FC<{
    children: ReactNode;
    showArrows: boolean;
    onArrowClick?: OnArrowClick;
    dataTn?: string;
    noGap?: boolean;
    carouselContainerRef?: RefObject<HTMLDivElement | null>;
}> = ({ children, showArrows, onArrowClick, dataTn, noGap, carouselContainerRef }) => {
    const { carouselOrientation, isFullWidth } = useInternalScrollCarousel();

    return (
        <div
            className={`${styles.wrapper} ${styles[carouselOrientation]} ${
                noGap ? styles.noGap : ''
            } ${isFullWidth ? styles.fullWidth : ''}`}
            data-tn={dataTn}
            ref={carouselContainerRef}
        >
            {showArrows && <Arrow type="previous" onArrowClick={onArrowClick} noInnerSpacing />}

            <ScrollContainer>{children}</ScrollContainer>

            {showArrows && <Arrow type="next" onArrowClick={onArrowClick} noInnerSpacing />}
        </div>
    );
};

export const InnerScrollCarousel: FC<
    {
        children: ReactNode;
        showArrows?: boolean;
        onArrowClick?: OnArrowClick;
        dataTn?: string;
        noGap?: boolean;
        snapAlign?: 'start' | 'center';
        shouldAdjustFlexBasis?: boolean;
        carouselContainerRef?: RefObject<HTMLDivElement | null>;
    } & ComponentProps<typeof InternalScrollCarouselProvider>
> = ({
    children,
    showArrows = false,
    onArrowClick,
    dataTn,
    noGap,
    snapAlign = 'start',
    shouldAdjustFlexBasis,
    carouselContainerRef,
    ...props
}) => {
    return (
        <InternalScrollCarouselProvider
            {...props}
            snapAlign={snapAlign}
            shouldAdjustFlexBasis={shouldAdjustFlexBasis}
        >
            <Inner
                showArrows={showArrows}
                onArrowClick={onArrowClick}
                dataTn={dataTn}
                noGap={noGap}
                carouselContainerRef={carouselContainerRef}
            >
                {children}
            </Inner>
        </InternalScrollCarouselProvider>
    );
};
